<template>
  <el-dialog
    :model-value="modelValue"
    :title="title"
    width="92vw"
    top="3vh"
    destroy-on-close
    class="export-excel-preview-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div v-loading="loading" class="excel-preview-shell">
      <p v-if="hint" class="excel-preview-hint">{{ hint }}</p>

      <el-empty v-if="!loading && !previewSheets.length" description="暂无 Sheet 可预览" />

      <template v-else-if="previewSheets.length">
        <el-tabs v-model="activeSheetIndex" type="card" class="excel-preview-tabs">
          <el-tab-pane
            v-for="(sheet, index) in previewSheets"
            :key="`${sheet.name}_${index}`"
            :label="sheet.name || `Sheet${index + 1}`"
            :name="String(index)"
          />
        </el-tabs>

        <div class="excel-preview-table-wrap">
          <table class="excel-preview-table" :style="tableThemeStyle">
            <tbody>
              <tr v-for="(row, ri) in activeGrid" :key="ri">
                <td
                  v-for="(col, ci) in row"
                  :key="ci"
                  :rowspan="col.rowspan"
                  :colspan="col.colspan"
                  :class="cellClass(col.cell)"
                >{{ formatPreviewCellValue(col.cell) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="excel-preview-footnote">示意预览：表头与列结构与导出 Excel 一致，数值为示例占位（0），非真实查询结果。</p>
      </template>
    </div>

    <template #footer>
      <div class="excel-preview-footer">
        <el-date-picker
          v-if="showExport"
          v-model="exportDateRange"
          type="daterange"
          unlink-panels
          value-format="YYYY-MM-DD"
          range-separator="~"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :shortcuts="dateShortcuts"
          style="width: 300px"
        />
        <div class="excel-preview-footer-actions">
          <el-button @click="emit('update:modelValue', false)">关闭</el-button>
          <el-button v-if="showApply" type="primary" plain :disabled="!config" @click="emit('apply')">载入到导出配置</el-button>
          <el-button
            v-if="showExport"
            type="primary"
            :disabled="!config || !exportDateRange?.[0]"
            :loading="exporting"
            @click="handleExport"
          >开始导出</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { buildPreviewSheetsFromConfig, buildSheetRenderGrid, formatPreviewCellValue } from '../reportDataExportPreview'
import { getThemeConfig } from '../reportDataExport'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title:      { type: String,  default: '导出预览' },
  config:     { type: Object,  default: null },
  loading:    { type: Boolean, default: false },
  hint:       { type: String,  default: '' },
  showApply:  { type: Boolean, default: false },
  showExport: { type: Boolean, default: false },
  defaultDateRange: { type: Array, default: () => [] },
  exporting:  { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'apply', 'export'])

const activeSheetIndex = ref('0')
const previewSheets = ref([])
const exportDateRange = ref([])

const dateShortcuts = [
  { text: '昨天', value: () => { const d = getRelDate(-1); return [d, d] } },
  { text: '近7天', value: () => [getRelDate(-7), getRelDate(-1)] },
  { text: '近30天', value: () => [getRelDate(-30), getRelDate(-1)] }
]

function getRelDate(offset) {
  const d = new Date()
  d.setDate(d.getDate() + offset)
  return d.toISOString().slice(0, 10)
}

watch(
  () => [props.config, props.modelValue, props.defaultDateRange],
  () => {
    if (!props.modelValue) return
    previewSheets.value = props.config ? buildPreviewSheetsFromConfig(props.config) : []
    activeSheetIndex.value = '0'
    exportDateRange.value = Array.isArray(props.defaultDateRange) && props.defaultDateRange[0]
      ? [...props.defaultDateRange]
      : [getRelDate(-7), getRelDate(-1)]
  },
  { immediate: true, deep: true }
)

function handleExport() {
  if (!props.config || !exportDateRange.value?.[0]) return
  emit('export', {
    config: props.config,
    dateRange: [...exportDateRange.value]
  })
}

const activeSheet = computed(() => {
  const idx = Number(activeSheetIndex.value) || 0
  return previewSheets.value[idx] || null
})

const activeGrid = computed(() => {
  return activeSheet.value ? buildSheetRenderGrid(activeSheet.value) : []
})

const tableThemeStyle = computed(() => {
  const theme = getThemeConfig(props.config?.exportForm?.themeKey || 'ocean')
  return {
    '--excel-header-bg': theme.headerFill,
    '--excel-subheader-bg': theme.subHeaderFill,
    '--excel-accent-bg': theme.accentFill,
    '--excel-header-color': theme.headerFontColor
  }
})

function cellClass(cell) {
  const role = cell?.role || 'body'
  return [`cell-role-${role}`]
}
</script>

<style scoped>
.excel-preview-shell {
  min-height: 200px;
}

.excel-preview-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
}

.excel-preview-tabs {
  margin-bottom: 8px;
}

.excel-preview-table-wrap {
  overflow: auto;
  max-height: 62vh;
  border: 1px solid #c5d4e8;
  border-radius: 4px;
  background: #fff;
  box-shadow: inset 0 0 0 1px #e8eef5;
}

.excel-preview-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
  font-family: 'Segoe UI', Calibri, Arial, sans-serif;
}

.excel-preview-table td {
  border: 1px solid #d4d4d4;
  padding: 4px 8px;
  white-space: nowrap;
  min-width: 56px;
  text-align: center;
  background: #fff;
  color: #1e293b;
}

.excel-preview-table .cell-role-header-main {
  background: var(--excel-header-bg, #dcebff);
  color: var(--excel-header-color, #1f3a5f);
  font-weight: 600;
}

.excel-preview-table .cell-role-header-sub {
  background: var(--excel-subheader-bg, #eef5ff);
  color: var(--excel-header-color, #1f3a5f);
  font-weight: 500;
}

.excel-preview-table .cell-role-label {
  background: var(--excel-accent-bg, #f8fafc);
  font-weight: 500;
  text-align: left;
}

.excel-preview-footnote {
  margin: 10px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.excel-preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
}

.excel-preview-footer-actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  margin-left: auto;
}
</style>
